import { Route, Redirect, withRouter } from 'react-router-dom';
import React, { Suspense, lazy, Fragment, useEffect, useState } from 'react';
import Loader from 'react-loaders';
import { connect } from 'react-redux';
import { get } from 'lodash';
import ReactCSSTransitionGroup from 'react-addons-css-transition-group';

import Notification from 'components/Notification';

import { checkAuth } from 'actions/Auth';
import { isBeautyCareAdmin } from 'actions/User';
import { getDistributors } from 'utils/isRentalUser';

import AppHeader from '../AppHeader';
import AppSidebar from '../AppSidebar';
import AppFooter from '../AppFooter';

import { ToastContainer } from 'react-toastify';
import { withTranslation } from 'react-i18next';

import Loading from 'components/Loading';
import { t } from 'i18n';

const AuthRedirect = lazy(() => import('pages/AuthRedirect'));
const AuthRedirectAdmin = lazy(() => import('pages/AuthRedirectAdmin'));
const DeleteAccount = lazy(() => import('pages/DeleteAccount'));

const UserList = lazy(() => import('pages/UserList'));
const LicenseDetails = lazy(() => import('pages/LicenseDetails'));
const PowerboxDetails = lazy(() => import('pages/PowerboxDetails'));
const CreatePowerbox = lazy(() => import('pages/CreatePowerbox'));
const LicenseTemplateDetails = lazy(() => import('pages/LicenseTemplateDetails'));
const CreateLicenseTemplate = lazy(() => import('pages/CreateLicenseTemplate'));
const CreateOrganization = lazy(() => import('pages/CreateOrganization'));
const CreateMember = lazy(() => import('pages/CreateMember'));
const LicenseModels = lazy(() => import('pages/LicenseModels'));
const LicenseList = lazy(() => import('pages/LicenseList'));
const MembersList = lazy(() => import('pages/MembersList'));
const MemberDetails = lazy(() => import('pages/MemberDetails'));
const Blacklist = lazy(() => import('pages/Blacklist'));
const OrganizationList = lazy(() => import('pages/OrganizationList'));
const OrganizationDetails = lazy(() => import('pages/OrganizationDetails'));
const PowerboxList = lazy(() => import('pages/PowerboxList'));
const CreateHomeUser = lazy(() => import('pages/CreateHomeUser'));
const CreateStudioUser = lazy(() => import('pages/CreateStudioUser'));
const CreateRentalUser = lazy(() => import('pages/CreateRentalUser'));
const UserDetails = lazy(() => import('pages/UserDetails'));
const LoginPage = lazy(() => import('pages/Login'));
const EMSUsageReports = lazy(() => import('pages/EMSUsageReports'));
const AppLogs = lazy(() => import('pages/AppLogs'));
const RentUserList = lazy(() => import('pages/RentUserList'));

const authPaths = {
  '/login': true,
  '/auth-redirect': true,
  '/auth-redirect-admin': true,
};

const publicPaths = {
  '/delete-account': true,
};

const checkUnauthorizedPath = (path, pathsToCheck) => {
  return Object.keys(pathsToCheck).reduce((acc, el) => {
    if (path.includes(el)) return true;
    return acc;
  }, false);
};

const AuthPageLoader = ({ message = t('common.loading') }) => (
  <div className="loader-container">
    <div className="loader-container-inner">
      <div className="text-center">
        <Loader type="ball-grid-beat" />
      </div>
      <h6 className="mt-3">{message}</h6>
    </div>
  </div>
);

const getAuthLoaderMessage = (pathname) => {
  if (pathname.includes('/auth-redirect-admin')) return t('auth.signingIn');
  if (pathname.includes('/auth-redirect')) return t('auth.checkingSession');
  if (pathname.includes('/login')) return t('auth.redirecting');
  return t('common.loading');
};

const AppMain = (props) => {
  const { checkAuth, checkIsBeautyCareAdmin, userRole, loading, i18n } = props;
  const currentLanguage = String(i18n.resolvedLanguage || i18n.language || 'en')
    .split('-')[0]
    .toLowerCase();
  const useWideSidebar = ['pt', 'ru', 'pl', 'cs', 'el', 'da'].includes(currentLanguage);
  const isAuthPath = checkUnauthorizedPath(props.location.pathname, authPaths);
  const isPublicPath = checkUnauthorizedPath(props.location.pathname, publicPaths);
  const [sessionCheckStarted, setSessionCheckStarted] = useState(false);
  const [sessionChecked, setSessionChecked] = useState(false);
  window.scrollTo(0, 0);
  useEffect(() => {
    if (isPublicPath) {
      setSessionCheckStarted(false);
      setSessionChecked(false);
      return;
    }

    checkAuth();
    setSessionCheckStarted(true);
  }, [checkAuth, isPublicPath]);
  useEffect(() => {
    if (!sessionCheckStarted) return;
    if (!loading) setSessionChecked(true);
  }, [loading, sessionCheckStarted]);
  useEffect(() => {
    if (!userRole) return;
    checkIsBeautyCareAdmin();
    if (userRole === 'admin') getDistributors();
  }, [checkIsBeautyCareAdmin, userRole]);

  if (isPublicPath) {
    return (
      <Suspense fallback={<AuthPageLoader />}>
        <Route exact path="/delete-account" component={DeleteAccount} />
      </Suspense>
    );
  }

  if (!sessionChecked && !isAuthPath) {
    return <AuthPageLoader message={t('auth.checkingSession')} />;
  }

  if (userRole === 'group_admin' && props.location.pathname === '/organizations') {
    return <Redirect to="/admins" />;
  }

  if (isAuthPath) {
    const message = getAuthLoaderMessage(props.location.pathname);
    return (
      <Suspense fallback={<AuthPageLoader message={message} />}>
        <AuthPageLoader message={message} />
        <Route path="/login" component={LoginPage} />
        <ToastContainer />
        <Notification />
        <Route path="/auth-redirect" component={AuthRedirect} />
        <Route path="/auth-redirect-admin" component={AuthRedirectAdmin} />
        <Loading loading={props.loading} />
      </Suspense>
    );
  }

  return (
    <Fragment>
      <ReactCSSTransitionGroup
        component="div"
        transitionName="TabsAnimation"
        transitionAppear={true}
        transitionAppearTimeout={0}
        transitionEnter={false}
        transitionLeave={false}
      >
        <div className={`d-container admin-container ${useWideSidebar ? 'd-container--wide-sidebar' : ''}`}>
          <div className="d-sidebar-base"></div>
          <AppSidebar />
          <div className="d-content">
            <AppHeader noTabs />
            <div className="app-main__outer">
              <div className="app-main__inner app-layout-main" key={i18n.resolvedLanguage || i18n.language}>
                <div className="">
                  <Suspense
                    fallback={
                      <div className="loader-container">
                        <div className="loader-container-inner">
                          <div className="text-center">
                            <Loader type="ball-grid-beat" />
                          </div>
                          <h6 className="mt-3">{t('common.loading')}</h6>
                        </div>
                      </div>
                    }
                  >
                    <Route exact path="/users" component={UserList} />
                    <Route exact path="/licenses" component={LicenseList} />
                    <Route exact path="/license-details/:id" component={LicenseDetails} />
                    <Route exact path="/admins" component={MembersList} />
                    <Route exact path="/license-types" component={LicenseModels} />
                    <Route exact path="/view-license-type/:id" component={LicenseTemplateDetails} />
                    <Route exact path="/create-license-type" component={CreateLicenseTemplate} />
                    <Route exact path="/create-home-user" component={CreateHomeUser} />
                    <Route exact path="/create-studio-user" component={CreateStudioUser} />
                    {(props.userRole === 'group_admin' || props.userRole === 'admin') && (
                      <Route exact path="/create-hybrid-user" component={CreateRentalUser} />
                    )}
                    <Route exact path="/create-organization" component={CreateOrganization} />
                    <Route
                      exact
                      path="/update-user/:id"
                      render={(routeProps) => <UserDetails {...routeProps} key={routeProps.match.params.id} />}
                    />
                    <Route
                      exact
                      path="/view-user/:id"
                      render={(routeProps) => <UserDetails {...routeProps} key={routeProps.match.params.id} />}
                    />
                    <Route exact path="/powerboxes" component={PowerboxList} />
                    {props.userRole === 'admin' && <Route exact path="/app-logs" component={AppLogs} />}
                    {(props.userRole === 'admin' || props.canCurrentUserCreateLicenses) && (
                      <>
                        <Route exact path="/update-powerbox/:id" component={PowerboxDetails} />
                        <Route exact path="/create-powerbox" component={CreatePowerbox} />
                      </>
                    )}
                    {props.userRole === 'group_admin' && <Route exact path="/hybrid-users" component={RentUserList} />}
                    {(props.userRole === 'admin' || props.userRole === 'group_admin') && (
                      <Route exact path="/ems-usage-reports" component={EMSUsageReports} />
                    )}
                    <Route exact path="/view-powerbox/:id" component={PowerboxDetails} />
                    {props.userRole !== 'chain_manager' && <Route exact path="/blacklists" component={Blacklist} />}
                    <Route exact path="/update-admin/:id" component={MemberDetails} />
                    <Route exact path="/view-admin/:id" component={MemberDetails} />
                    <Route exact path="/create-admin" component={CreateMember} />
                    {props.userRole !== 'group_admin' && (
                      <Route exact path="/organizations" component={OrganizationList} />
                    )}
                    <Route exact path="/update-organization/:id" component={OrganizationDetails} />

                    {props.userRole !== 'group_admin' && (
                      <Route exact path="/" render={() => <Redirect to="/organizations" />} />
                    )}

                    {props.userRole === 'group_admin' && (
                      <Route exact path="/" render={() => <Redirect to="/admins" />} />
                    )}
                  </Suspense>
                </div>
              </div>
            </div>
          </div>
        </div>
      </ReactCSSTransitionGroup>

      <ToastContainer />
      <Notification />
      <Loading loading={props.loading} />
      <AppFooter />
    </Fragment>
  );
};

const mapStateToProps = ({ UserRoles, Loading, Theme, Auth }) => ({
  userRole: UserRoles.userRole,
  loading: Loading.loading,
  darkTheme: Theme.darkTheme,
  canCurrentUserCreateLicenses: get(Auth, 'canCurrentUserCreateLicenses'),
});

const mapDispatchToProps = (dispatch) => ({
  checkAuth: () => dispatch(checkAuth.request()),
  setDarkTheme: (payload) => dispatch({ type: 'SET_THEME', payload }),
  checkIsBeautyCareAdmin: () => dispatch(isBeautyCareAdmin.request()),
});

export default withTranslation()(connect(mapStateToProps, mapDispatchToProps)(withRouter(AppMain)));
